<template>
	<component v-if="hasContent" :is="componentOfRootPart(ui)" :part="rootPartRef(ui)" :data="data"
			   :readonly="roValue" :viewmode="vmValue"
			   :actions="actions" :display="display" :errs="errs"
			   @close="forwardClose" ref="mountedComponent"></component>
	<slot v-else></slot>
</template>

<script setup lang="ts">
import {ref, computed, provide, onBeforeMount} from "vue";
import type {FormPartOrReference} from "@t/fd/FormPartOrReference.ts";
import {componentOfPart, partRef} from "@fd/ComponentResolver.ts";
import usePart, {PartProps} from "@fd/Part.ts";
import type {FormPart} from "@t/fd/FormPart.ts";
import type {DialogDisplay} from "@t/ui/Dialog.ts";
import {componentOfPartKey, ErrorMap, errorMapKey, executeKey, findCategoryKey, findListKey, formDataKey, interfaceRootKey, partRefKey, pathKey, provideLib} from "@fd/PartInjectKeys.ts";
import type {InterfaceRoot, RootLayout} from "@t/fd/InterfaceRoot.ts";
import {injectEnv, injectEventBus} from "@fd/Environment.ts";
import provideMaskingLib from "@fd/Masking.ts";
import bakeLib from "@fd/ScriptLib.ts";
import {useListStore} from "@/store/ListStore.ts";
import {secureRootKey} from "@t/security/ObjectAccess.ts";
import type {FormMethodCompiled} from "@t/fd/layout/FormDefinition.ts";

interface InterfaceRootProps extends PartProps {
	part: InterfaceRoot<RootLayout>,
	actions?: FormPart,
	display?: DialogDisplay,
	errs?: ErrorMap
}

const props = withDefaults(defineProps<InterfaceRootProps>(), {
	display: () => <DialogDisplay>{ mode: "form" },
	errs: () => []
});

const emit = defineEmits(["execute", "close"]);
const root = computed<InterfaceRoot<RootLayout>>(() => props.part);
const hasContent = computed<boolean>(() => typeof props.part?.content === "object");
const ui = computed<RootLayout>(() => props.part.content as RootLayout);

const env = injectEnv();
const ls = useListStore();
provide(formDataKey, props.data);
provide(secureRootKey, props.data);	// root for ObjectAccess permissions
provide(findListKey, ls.findList);
provide(findCategoryKey, ls.findCategory);

/** Prepare library to support field masking */
const mlib = provideMaskingLib(root, props.data);
/** Prepare execution library and execute root script */
const exeLib: any = provideLib(bakeLib(root.value.script, env, props.part, props.data));

const roOverride = ref<boolean|null>(null);
const vmOverride = ref<boolean|null>(null);
const {vm, ro} = usePart(props as InterfaceRootProps, root, props.data, exeLib, props.errs);

const roValue = computed(() => roOverride.value === null ? ro.value : roOverride.value);
const vmValue = computed(() => vmOverride.value === null ? vm.value : vmOverride.value);

provide(pathKey, "");   // root path is empty
provide(errorMapKey, props.errs);


exeLib["findList"] = ls.findList;
exeLib["findCategory"] = ls.findCategory;
const partRefFn = (ref: FormPartOrReference) => partRef(ref, root.value);
provide(partRefKey, partRefFn);
exeLib["partRef"] = partRefFn;
provide(componentOfPartKey, (pr: FormPartOrReference) => componentOfPart(pr, (ref) => partRef(ref, root.value)));
provide(interfaceRootKey, root);

const bus = injectEventBus();

const executeMethod = (funcName: string, data: object, localPart: FormPart, ...extraArgs: any):any => {
	let fn = root.value.methods ? root.value.methods[funcName] : undefined;
	if (fn === undefined) {
		return new Promise((resolve, reject) => {
			emit("execute", funcName, resolve, reject, ...extraArgs);
			const busResult = bus.fire("execute", funcName, data, localPart, ...extraArgs);
			if (busResult instanceof Promise) {
				// Handling as promise
				busResult.then(resolve, reject);
			} else if (busResult !== undefined) {
				// Handling as a fixed result
				resolve(busResult);
			}
		});
	}

	if (typeof fn === "string") {
		// Compile function
		const params = ["g", "o", "f", "p", "r", "lib", "env", "...extra"];
		params.push(fn); // function body
		root.value.methods[funcName] = fn = new Function(...params) as FormMethodCompiled;
	}
	const f = root.value.content;
	return fn(props.data, data, f, localPart, root.value, exeLib, env, ...extraArgs);
};
provide(executeKey, executeMethod);
exeLib["execute"] = executeMethod;

function rootPartRef(ref: FormPartOrReference): FormPart {
	return partRef(ref, props.part);
}

function componentOfRootPart(pr: FormPartOrReference) {
	return componentOfPart(pr, (ref) => partRef(ref, props.part));
}

function forwardClose(...args:any) {
	emit('close', ...args);
}

onBeforeMount(() => {
	// Install list and categories contained in the form
	if (root.value.lists) {
		for (let listName of Object.keys(root.value.lists)) {
			const list = root.value.lists[listName];
			ls.installList(listName, list);
		}
	}

	if (root.value.categories) {
		for (let catName of Object.keys(root.value.categories)) {
			const ct = root.value.categories[catName];
			ls.installCategory(catName, ct);
		}
	}
});

function setReadOnly(flag:boolean|null):void {
	roOverride.value = flag;
}

function setViewMode(flag:boolean|null):void {
	vmOverride.value = flag;
}

const mountedComponent = ref(null);

function setDirty(dirtyFlag:boolean):void {
	var fn = mountedComponent.value?.setDirty;
	if (typeof fn === "function") fn(dirtyFlag);
}

function closeDialog() {
	var fn = mountedComponent.value?.closeDialog;
	if (typeof fn === "function") fn();
}

defineExpose({
	setDirty,
	setReadOnly,
	setViewMode,
	/** Animated close of dialog window */
	closeDialog
});
</script>

<style>

</style>